(() => {
  if (customElements.get('rideout-lp-steps')) return;

  const SCROLL_GAP = 24;
  const READING_LINE = 0.3;

  function sectionOf(element) {
    return element.closest('.shopify-section') || element;
  }

  class RideoutLpSteps extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.update = this.update.bind(this);
      this.frame = null;
      this.visible = new Set();
    }

    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-step]'))
        .map((link) => ({ link, target: document.getElementById(link.dataset.step) }))
        .filter((step) => step.target);
      if (!this.steps.length) return;

      this.endElement = this.findEnd();
      this.addEventListener('click', this.onClick);

      this.article = new Set([this.endElement]);
      let node = sectionOf(this.steps[0].target);
      while (node && node !== this.endElement) {
        this.article.add(node);
        node = node.nextElementSibling;
      }

      // Siblings are observed too: a jump that skips the whole article (anchor link,
      // restored scroll position) still triggers a callback and re-evaluates the state.
      const container = this.endElement.parentElement;
      const observed = container ? Array.from(container.children) : [];
      this.article.forEach((element) => {
        if (!observed.includes(element)) observed.push(element);
      });

      this.observer = new IntersectionObserver(this.onIntersect);
      observed.forEach((element) => this.observer.observe(element));

      window.addEventListener('resize', this.onScroll);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    findEnd() {
      const id = this.dataset.endTarget;
      if (id) {
        const element = document.getElementById(id);
        if (element) return element;
      }
      const lastSection = sectionOf(this.steps[this.steps.length - 1].target);
      return lastSection.nextElementSibling || lastSection;
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (!this.article.has(entry.target)) return;
        if (entry.isIntersecting) this.visible.add(entry.target);
        else this.visible.delete(entry.target);
      });

      // Track scroll only while part of the article is on screen.
      if (this.visible.size) window.addEventListener('scroll', this.onScroll, { passive: true });
      else window.removeEventListener('scroll', this.onScroll);

      this.update();
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    navBottom() {
      return Math.max(this.getBoundingClientRect().bottom, 0);
    }

    // Sticky top of the wrapper: non-zero when a sticky header sits above the nav.
    stickyTop() {
      return parseFloat(getComputedStyle(sectionOf(this)).top) || 0;
    }

    update() {
      const navBottom = this.navBottom();
      const line = navBottom + (window.innerHeight - navBottom) * READING_LINE;
      const starts = this.steps.map((step) => sectionOf(step.target).getBoundingClientRect().top);
      const end = this.endElement.getBoundingClientRect().bottom;
      let activeIndex = -1;

      this.steps.forEach((step, index) => {
        const start = starts[index];
        const stop = index < starts.length - 1 ? starts[index + 1] : end;
        const progress = Math.min(Math.max((line - start) / Math.max(stop - start, 1), 0), 1);

        if (line >= start && line < stop) activeIndex = index;
        step.link.style.setProperty('--rideout-step-progress', progress.toFixed(4));
      });

      this.steps.forEach((step, index) => {
        const isActive = index === activeIndex;
        step.link.classList.toggle('is-active', isActive);
        step.link.classList.toggle('is-done', activeIndex === -1 ? line >= end : index < activeIndex);
        if (isActive) step.link.setAttribute('aria-current', 'step');
        else step.link.removeAttribute('aria-current');
      });

      if (activeIndex === -1 && line < starts[0]) {
        this.steps[0].link.classList.add('is-active');
      }

      const top = this.stickyTop();
      this.classList.toggle('is-visible', starts[0] <= top && end > top + this.offsetHeight);
    }

    onClick(event) {
      const link = event.target instanceof Element ? event.target.closest('[data-step]') : null;
      const step = link && this.steps.find((item) => item.link === link);
      if (!step) return;

      event.preventDefault();
      const anchor = step.target.querySelector('[data-point-anchor]') || step.target;
      const top =
        anchor.getBoundingClientRect().top +
        window.pageYOffset -
        this.stickyTop() -
        this.offsetHeight -
        SCROLL_GAP;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  customElements.define('rideout-lp-steps', RideoutLpSteps);
})();
